import React, { useState, useEffect } from 'react'
import { Box, CircularProgress } from '@mui/material'
import TutorialPage from './TutorialPage'
import RoadmapCockpit from './RoadmapCockpit'
import TransitionScreen from './workflows/TransitionScreen'
import AddIdeaPopup from './workflows/AddIdeaPopup'
import TimeProjectionPopup from './workflows/TimeProjectionPopup'
import EngagementPopup from './workflows/EngagementPopup'
import { roadmapAPI } from './api/roadmapAPI'
import { useLocation, useNavigate } from 'react-router-dom'

const Roadmap = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [hasIdeas, setHasIdeas] = useState(false)
  const [showTutorial, setShowTutorial] = useState(true)

  // Workflow states
  const [showTransition, setShowTransition] = useState(false)
  const [showAddIdea, setShowAddIdea] = useState(false)
  const [showTimeProjection, setShowTimeProjection] = useState(false)
  const [showEngagement, setShowEngagement] = useState(false)
  const [currentIdeaData, setCurrentIdeaData] = useState(null)
  const [currentHorizon, setCurrentHorizon] = useState(null)

  useEffect(() => {
    checkIdeas()
  }, [])

  useEffect(() => {
    if (loading) return

    const searchParams = new URLSearchParams(location.search)
    const urlAction = searchParams.get('action') || searchParams.get('modal')
    if (urlAction !== 'new-dream') return
    if (hasIdeas) return

    setShowTutorial(false)
    setShowTransition(false)
    setShowAddIdea(true)
    navigate(location.pathname, { replace: true })
  }, [hasIdeas, loading, location.pathname, location.search, navigate])

  const checkIdeas = async () => {
    try {
      const data = await roadmapAPI.getIdeasByHorizon()
      const totalIdeas =
        (data.dreams?.length || 0) +
        (data.this_year?.length || 0) +
        (data.next_year?.length || 0) +
        (data.year_5?.length || 0) +
        (data.year_10?.length || 0) +
        (data.completed?.length || 0)

      setHasIdeas(totalIdeas > 0)
      setShowTutorial(totalIdeas === 0)
    } catch (error) {
      console.error('Error checking ideas:', error)
      setHasIdeas(false)
      setShowTutorial(true)
    } finally {
      setLoading(false)
    }
  }

  const handleStartWorkflow = () => {
    // Flow: Tutorial → Transition Screen (WITH TRANSITION PAGE) → Add Idea
    setShowTutorial(false)
    setShowTransition(true)
  }

  const handleTransitionStart = () => {
    // Flow: Transition Screen → Add Idea Popup
    setShowTransition(false)
    setShowAddIdea(true)
  }

  const handleAddIdeaSubmit = (data) => {
    setCurrentIdeaData(data)
    setShowAddIdea(false)
    setShowTimeProjection(true)
  }

  const handleTimeProjectionSubmit = async (horizon) => {
    try {
      const ideaData = {
        ...currentIdeaData,
        horizon: horizon || 'DREAMS',
      }

      await roadmapAPI.createIdea(ideaData)

      // Refresh and show cockpit
      await checkIdeas()

      // Flow: Workflow creation idea → Enrich (IF N or N+1) → Sub element creation
      // Show engagement if needed (Enrich step - only for YEAR_CURRENT or YEAR_NEXT)
      if (horizon === 'YEAR_CURRENT' || horizon === 'YEAR_NEXT') {
        // Get the created idea to pass to engagement
        const ideasData = await roadmapAPI.getIdeasByHorizon()
        const allIdeas = [
          ...(ideasData.dreams || []),
          ...(ideasData.this_year || []),
          ...(ideasData.next_year || []),
          ...(ideasData.year_5 || []),
          ...(ideasData.year_10 || []),
        ]
        const newIdea = allIdeas.find(
          (i) => i.title === currentIdeaData.title
        )
        if (newIdea) {
          setCurrentIdeaData(newIdea)
          setShowEngagement(true) // Enrich step → Sub element creation (project/task/objectif)
        } else {
          resetWorkflow()
        }
      } else {
        resetWorkflow()
      }
    } catch (error) {
      console.error('Error creating idea:', error)
      resetWorkflow()
    }
  }

  const handleEngagementClose = () => {
    resetWorkflow()
  }

  const resetWorkflow = () => {
    setShowTimeProjection(false)
    setShowEngagement(false)
    setCurrentIdeaData(null)
    setCurrentHorizon(null)
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: '#00001d',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (showTutorial) {
    return (
      <>
        <TutorialPage onStart={handleStartWorkflow} />
        <TransitionScreen
          open={showTransition}
          onStart={handleTransitionStart}
        />
        <AddIdeaPopup
          open={showAddIdea}
          onClose={() => {
            setShowAddIdea(false)
            setCurrentIdeaData(null)
          }}
          onSubmit={handleAddIdeaSubmit}
        />
        <TimeProjectionPopup
          open={showTimeProjection}
          onClose={() => {
            setShowTimeProjection(false)
            setCurrentIdeaData(null)
          }}
          onSubmit={handleTimeProjectionSubmit}
          onBack={() => {
            setShowTimeProjection(false)
            setShowAddIdea(true)
          }}
        />
        <EngagementPopup
          open={showEngagement}
          onClose={handleEngagementClose}
          ideaId={currentIdeaData?.id}
          onLinkCreated={() => {
            checkIdeas()
            handleEngagementClose()
          }}
        />
      </>
    )
  }

  return (
    <RoadmapCockpit
      onStartWorkflow={handleStartWorkflow}
      skipTransition={true}
      openAddIdeaFromUrl={new URLSearchParams(location.search).get('action') === 'new-dream'}
      onAddIdeaUrlConsumed={() => navigate(location.pathname, { replace: true })}
    />
  )
}

export default Roadmap
